import { CurrencyAmount, Ether, MaxUint256, Percent, Token } from '@uniswap/sdk-core'
import { TickMath } from '@uniswap/v3-sdk'
import { DYNAMIC_FEE_FLAG, Pool, Position, V4PositionManager } from '@uniswap/v4-sdk'
import {
  CreateLPPositionRequest,
  CreateLPPositionResponse,
  IncreaseLPPositionRequest,
  IncreaseLPPositionResponse,
} from 'uniswap/src/data/tradingApi/__generated__'
import { getUniswapWagmiPublicClient } from 'uniswap/src/features/providers/wagmiBridge'
import {
  DELI_HOOK_CP_ABI,
  DELI_HOOK_CP_ADDRESS,
  POSITION_MANAGER_ADAPTER_ADDRESS,
  V4_POOL_MANAGER_ABI,
  V4_POSITION_MANAGER_ABI,
  ZERO_ADDRESS,
  ZERO_BYTES32,
  alignTicks,
  buildUseNative,
  estimateGasFee,
  fetchDecimals,
  getV4PoolManagerAddress,
  getV4PositionManagerAddress,
  readConstantProductReserves,
  readPoolState,
} from 'uniswap/src/features/transactions/liquidity/steps/shared'
import { parseErrorMessageTitle } from 'uniswap/src/features/transactions/liquidity/utils'
import { OnChainTransactionFields, TransactionStepType } from 'uniswap/src/features/transactions/steps/types'
import {
  ValidatedTransactionRequest,
  validateTransactionRequest,
} from 'uniswap/src/features/transactions/swap/utils/trade'
import { logger } from 'utilities/src/logger/logger'
import { encodeFunctionData } from 'viem'

const CP_INITIAL_TICK_SPACING = 1
const CP_INITIAL_SQRT_PRICE_X96 = BigInt('79228162514264337593543950336')

export interface IncreasePositionTransactionStep extends OnChainTransactionFields {
  // Doesn't require permit
  type: TransactionStepType.IncreasePositionTransaction
}

export interface InitializePoolTransactionStep extends OnChainTransactionFields {
  type: TransactionStepType.InitializePoolTransaction
}

export interface SubscribePositionTransactionStep extends OnChainTransactionFields {
  type: TransactionStepType.SubscribePositionTransaction
}

export interface SubscribePositionTransactionStepAsync {
  type: TransactionStepType.SubscribePositionTransactionAsync
  getTxRequest(prevHash: string): Promise<ValidatedTransactionRequest | undefined>
}

export interface IncreasePositionTransactionStepAsync {
  // Requires permit
  type: TransactionStepType.IncreasePositionTransactionAsync
  getTxRequest(signature: string): Promise<ValidatedTransactionRequest | undefined>
}

export function createIncreasePositionStep(txRequest: ValidatedTransactionRequest): IncreasePositionTransactionStep {
  return {
    type: TransactionStepType.IncreasePositionTransaction,
    txRequest,
  }
}

export function createInitializePoolStep(
  txRequest: ValidatedTransactionRequest | undefined,
): InitializePoolTransactionStep | undefined {
  if (!txRequest) {
    return undefined
  }

  return {
    type: TransactionStepType.InitializePoolTransaction,
    txRequest,
  }
}

export function createSubscribePositionAsyncStep(params: {
  chainId: number
  walletAddress: `0x${string}`
}): SubscribePositionTransactionStepAsync {
  const { chainId, walletAddress } = params
  return {
    type: TransactionStepType.SubscribePositionTransactionAsync,
    getTxRequest: async (prevHash: string): Promise<ValidatedTransactionRequest | undefined> => {
      if (!prevHash) return undefined
      const client = getUniswapWagmiPublicClient(chainId)
      if (!client) throw new Error('Missing viem public client')

      // Wait for the previous tx (add liquidity) to confirm, then parse ERC721 Transfer
      try {
        // eslint-disable-next-line no-console
        console.log('lp:subscribe:waitReceipt', { prevHash })
      } catch {}
      let receipt
      try {
        receipt = await client.waitForTransactionReceipt({ hash: prevHash as `0x${string}` })
      } catch (error) {
        try {
          // eslint-disable-next-line no-console
          console.error('lp:subscribe:waitReceiptError', {
            prevHash,
            prevHashLength: prevHash?.length,
            error,
          })
        } catch {}
        throw error
      }
      try {
        // eslint-disable-next-line no-console
        console.log('lp:subscribe:receipt', {
          txHash: prevHash,
          blockNumber: receipt.blockNumber,
          status: receipt.status,
          logCount: receipt.logs.length,
          contractAddress: receipt.contractAddress,
        })
      } catch {}
      const positionManager = getV4PositionManagerAddress(chainId)
      const TRANSFER_TOPIC = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef'
      const toTopic = `0x000000000000000000000000${walletAddress.slice(2).toLowerCase()}`

      const log = receipt.logs.find((l) => {
        return (
          typeof l?.address === 'string' &&
          l.address.toLowerCase() === positionManager.toLowerCase() &&
          Array.isArray(l.topics) &&
          l.topics.length >= 4 &&
          (l.topics[0] || '').toLowerCase() === TRANSFER_TOPIC &&
          (l.topics[2] || '').toLowerCase() === toTopic
        )
      })

      if (!log) {
        try {
          // eslint-disable-next-line no-console
          console.error('lp:subscribe:noTransferLog', {
            expectedContract: positionManager,
            logCount: receipt.logs.length,
            topicsPreview: receipt.logs.slice(0, 5).map((entry) => ({
              address: entry.address,
              topics: entry.topics,
            })),
          })
        } catch {}
        throw new Error('Unable to locate minted position NFT in receipt')
      }

      const tokenIdTopic = log.topics?.[3]
      if (!tokenIdTopic) {
        throw new Error('Missing tokenId topic in receipt log')
      }
      const tokenId = BigInt(tokenIdTopic)
      try {
        // eslint-disable-next-line no-console
        console.log('lp:subscribe:transferLog', { address: log.address, topics: log.topics })
      } catch {}
      try {
        // eslint-disable-next-line no-console
        console.log('lp:subscribe:tokenId', tokenId.toString())
      } catch {}

      if (!log.topics || log.topics.length < 4) {
        try {
          // eslint-disable-next-line no-console
          console.error('lp:subscribe:invalidTransferTopics', {
            topics: log.topics,
            data: log.data,
          })
        } catch {}
        throw new Error('Transfer log missing tokenId topic')
      }

      const subscribeCalldata = encodeFunctionData({
        abi: V4_POSITION_MANAGER_ABI,
        functionName: 'subscribe',
        args: [tokenId, POSITION_MANAGER_ADAPTER_ADDRESS, ZERO_BYTES32],
      }) as `0x${string}`

      const tx = {
        chainId,
        from: walletAddress,
        to: positionManager,
        data: subscribeCalldata,
        value: undefined,
      }

      return validateTransactionRequest(tx as any)
    },
  }
}

export function createCreatePositionAsyncStep(
  createPositionRequestArgs: CreateLPPositionRequest | undefined,
): IncreasePositionTransactionStepAsync {
  return {
    type: TransactionStepType.IncreasePositionTransactionAsync,
    getTxRequest: async (signature: string): Promise<ValidatedTransactionRequest | undefined> => {
      if (!createPositionRequestArgs) {
        return undefined
      }

      try {
        const tx = await buildV4CreatePositionTx(createPositionRequestArgs, signature)
        return validateTransactionRequest(tx)
      } catch (e) {
        const message = parseErrorMessageTitle(e, { includeRequestId: true })
        if (message) {
          logger.error(message, {
            tags: {
              file: 'increasePosition',
              function: 'createCreatePositionAsyncStep',
            },
          })

          if (createPositionRequestArgs) {
          }
        }
        throw new Error('create failed to get transaction request', {
          cause: message,
        })
      }
    },
  }
}

export function createIncreasePositionAsyncStep(
  increasePositionRequestArgs: IncreaseLPPositionRequest | undefined,
): IncreasePositionTransactionStepAsync {
  return {
    type: TransactionStepType.IncreasePositionTransactionAsync,
    getTxRequest: async (signature: string): Promise<ValidatedTransactionRequest | undefined> => {
      if (!increasePositionRequestArgs) {
        return undefined
      }

      try {
        const tx = await buildV4IncreasePositionTx(increasePositionRequestArgs, signature)
        return validateTransactionRequest(tx)
      } catch (e) {
        const message = parseErrorMessageTitle(e, { includeRequestId: true })
        if (message) {
          logger.error(message, {
            tags: {
              file: 'generateTransactionSteps',
              function: 'createIncreasePositionAsyncStep',
            },
          })
          if (increasePositionRequestArgs) {
          }
        }
        throw new Error('increase failed to get transaction request', {
          cause: message,
        })
      }
    },
  }
}

// -------------------------------
// v4 SDK builders (create/increase)
// -------------------------------

export async function buildV4CreatePositionResponse(
  args: CreateLPPositionRequest,
  signature?: string,
): Promise<CreateLPPositionResponse> {
  console.log('buildV4CreatePositionResponse', args)
  // Build viem client for the target chain
  const chainId = Number(args.chainId)
  const walletAddress = args.walletAddress as `0x${string}`
  const client = getUniswapWagmiPublicClient(chainId)
  if (!client) throw new Error('Missing viem public client')

  // Pool key
  const poolReq = args.position?.pool
  if (!poolReq) throw new Error('Missing pool in args')
  const token0Addr = poolReq.token0 as `0x${string}`
  const token1Addr = poolReq.token1 as `0x${string}`
  const fee = Number(poolReq.fee)
  const tickSpacing = Number(poolReq.tickSpacing)
  const hooks = (poolReq as any).hooks as `0x${string}` | undefined

  // Tokens
  const currency0 =
    token0Addr === ZERO_ADDRESS
      ? Ether.onChain(chainId)
      : new Token(chainId, token0Addr, await fetchDecimals(client, token0Addr))
  const currency1 =
    token1Addr === ZERO_ADDRESS
      ? Ether.onChain(chainId)
      : new Token(chainId, token1Addr, await fetchDecimals(client, token1Addr))

  // Pool state for create vs existing
  let sqrtRatioX96Str: string | undefined
  let currentTick: number | undefined
  let poolLiquidityStr: string | undefined
  const needsSeparateInitialization = Boolean((args as any)?.initializePoolSeparately)
  const initialSqrtP =
    ((args as any)?.initializationSqrtPriceX96 as string | undefined) ?? (args.initialPrice as string | undefined)
  const hookAddress = needsSeparateInitialization ? DELI_HOOK_CP_ADDRESS : hooks ?? ZERO_ADDRESS
  const creatingPool = Boolean(initialSqrtP)
  if (creatingPool) {
    // Use UI-provided initial price only
    const sqrtX96 = String(initialSqrtP)
    sqrtRatioX96Str = sqrtX96
    try {
      // TickMath from v3-sdk expects JSBI; use string -> JSBI
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const JSBI = require('jsbi')
      currentTick = Number(TickMath.getTickAtSqrtRatio(JSBI.BigInt(sqrtX96)))
    } catch {
      currentTick = 0
    }
    poolLiquidityStr = '0'
  } else {
    try {
      const state = await readPoolState(
        client as any,
        chainId,
        currency0 as any,
        currency1 as any,
        fee,
        tickSpacing,
        hookAddress,
      )
      sqrtRatioX96Str = state.sqrtRatioX96
      currentTick = state.currentTick
      poolLiquidityStr = state.poolLiquidity
    } catch {}
  }

  // Ticks
  const providedTickLower = args.position?.tickLower
  const providedTickUpper = args.position?.tickUpper
  if (!needsSeparateInitialization && (providedTickLower === undefined || providedTickUpper === undefined)) {
    throw new Error('tickLower/tickUpper are required from UI')
  }
  const rawTickLower = Number(providedTickLower)
  const rawTickUpper = Number(providedTickUpper)
  const { tickLower, tickUpper } = alignTicks(rawTickLower, rawTickUpper, tickSpacing)

  // Build a pool instance for math (use initial price if present, else fetched state)
  const poolForMath = new Pool(
    currency0,
    currency1,
    fee,
    tickSpacing,
    hookAddress,
    initialSqrtP ?? sqrtRatioX96Str ?? '0',
    poolLiquidityStr ?? '0',
    currentTick ?? 0,
  )

  // Amount mapping: derive dependent if not provided
  const independentToken = (args as any).independentToken as 'TOKEN_0' | 'TOKEN_1' | undefined
  const independentAmount = args.independentAmount as string | undefined
  const initialDependentAmount = (args as any).initialDependentAmount as string | undefined

  let finalAmount0 = '0'
  let finalAmount1 = '0'
  let dependentAmount: string | undefined
  if (independentToken && independentAmount && BigInt(independentAmount) > BigInt(0)) {
    if (initialSqrtP && initialDependentAmount) {
      // Creation path: trust UI-provided dependent for initial price
      if (independentToken === 'TOKEN_0') {
        finalAmount0 = independentAmount
        finalAmount1 = initialDependentAmount
        dependentAmount = initialDependentAmount
      } else {
        finalAmount1 = independentAmount
        finalAmount0 = initialDependentAmount
        dependentAmount = initialDependentAmount
      }
    } else {
      // Existing or creation path without initialDependent: derive dependent via sentinel MaxUint256
      const maxStr = MaxUint256.toString()
      const amount0 = CurrencyAmount.fromRawAmount(
        currency0,
        independentToken === 'TOKEN_0' ? independentAmount : maxStr,
      )
      const amount1 = CurrencyAmount.fromRawAmount(
        currency1,
        independentToken === 'TOKEN_1' ? independentAmount : maxStr,
      )
      const tempPos = Position.fromAmounts({
        pool: poolForMath,
        tickLower,
        tickUpper,
        amount0: amount0.quotient,
        amount1: amount1.quotient,
        useFullPrecision: true,
      })
      if (independentToken === 'TOKEN_0') {
        finalAmount0 = independentAmount
        finalAmount1 = tempPos.amount1.quotient.toString()
        dependentAmount = finalAmount1
      } else {
        finalAmount1 = independentAmount
        finalAmount0 = tempPos.amount0.quotient.toString()
        dependentAmount = finalAmount0
      }
    }
  } else {
    // Fallback to explicit amounts if provided by UI
    finalAmount0 = args.amount0 ?? '0'
    finalAmount1 = args.amount1 ?? '0'
  }

  // Build final position with both amounts
  const position = Position.fromAmounts({
    pool: poolForMath,
    tickLower,
    tickUpper,
    amount0: CurrencyAmount.fromRawAmount(currency0, finalAmount0).quotient,
    amount1: CurrencyAmount.fromRawAmount(currency1, finalAmount1).quotient,
    useFullPrecision: true,
  })

  // Slippage/deadline
  const slippagePct = new Percent(Math.floor(Number((args as any).slippageTolerance ?? 0) * 100), 10_000)
  const latestBlock = await client.getBlock()
  const nowTs = Number(latestBlock.timestamp)
  const deadlineSec = (args as any).deadline ?? nowTs + 20 * 60

  const useNative = buildUseNative(chainId, currency0, currency1)

  let initializeTx:
    | {
        to: string
        from: string
        data: string
        value: string
        chainId: any
      }
    | undefined
  let gasFeeInitialize: string | undefined
  let gasFee: string | undefined
  let createTx:
    | {
        to: string
        from: string
        data: string
        value: string
        chainId: any
      }
    | undefined

  if (needsSeparateInitialization) {
    const initializeCalldata = encodeFunctionData({
      abi: V4_POOL_MANAGER_ABI,
      functionName: 'initialize',
      args: [
        {
          currency0: token0Addr,
          currency1: token1Addr,
          fee,
          tickSpacing: CP_INITIAL_TICK_SPACING,
          hooks: hookAddress,
        },
        CP_INITIAL_SQRT_PRICE_X96,
      ],
    }) as `0x${string}`

    const initTx = {
      to: getV4PoolManagerAddress(chainId),
      from: walletAddress,
      data: initializeCalldata,
      value: '0',
      chainId: chainId as any,
    }

    if (args.simulateTransaction) {
      gasFeeInitialize = await estimateGasFee(
        client as any,
        walletAddress,
        getV4PoolManagerAddress(chainId),
        initializeCalldata,
      )
    }

    initializeTx = initTx
    gasFee = gasFeeInitialize
    createTx = undefined
  }

  if (!needsSeparateInitialization) {
    const mintOptions: any = {
      recipient: walletAddress,
      slippageTolerance: slippagePct,
      deadline: String(deadlineSec),
      hookData: '0x',
      useNative,
      ...(initialSqrtP ? { createPool: true, sqrtPriceX96: initialSqrtP } : {}),
    }

    const batchPermitData = (args as any)?.batchPermitData
    if (signature && batchPermitData?.values && walletAddress) {
      mintOptions.batchPermit = {
        owner: walletAddress,
        permitBatch: batchPermitData.values,
        signature,
      }
    }

    const { calldata, value } = V4PositionManager.addCallParameters(position, mintOptions)
    console.log('calldata', calldata)

    if (args.simulateTransaction) {
      gasFee = await estimateGasFee(
        client as any,
        walletAddress,
        getV4PositionManagerAddress(chainId),
        calldata as `0x${string}`,
        value ? BigInt(value as any) : undefined,
      )
    }

    createTx = {
      to: getV4PositionManagerAddress(chainId),
      from: walletAddress,
      data: calldata as string,
      value: value ? String(value) : '0',
      chainId: chainId as any,
    }
  }

  const response = {
    requestId: undefined,
    initialize: initializeTx,
    create: createTx,
    gasFee,
    gasFeeInitialize,
    dependentAmount,
    poolLiquidity: poolLiquidityStr,
    currentTick,
    sqrtRatioX96: initialSqrtP ?? sqrtRatioX96Str,
  } as CreateLPPositionResponse & {
    initialize?: typeof initializeTx
    gasFeeInitialize?: string
  }

  return response
}

export async function buildV4IncreasePositionResponse(
  args: IncreaseLPPositionRequest,
  signature?: string,
): Promise<IncreaseLPPositionResponse> {
  console.log('buildV4IncreasePositionResponse', args)
  const chainId = Number(args.chainId)
  const walletAddress = args.walletAddress as `0x${string}`
  const client = getUniswapWagmiPublicClient(chainId)
  if (!client) throw new Error('Missing viem public client')

  // Pool/position inputs
  const poolReq = args.position?.pool
  if (!poolReq) throw new Error('Missing pool in args')
  const token0Addr = poolReq.token0 as `0x${string}`
  const token1Addr = poolReq.token1 as `0x${string}`
  const tickSpacing = Number(poolReq.tickSpacing)
  const hooks = (poolReq as any).hooks as `0x${string}` | undefined
  const isConstantProductHook = hooks?.toLowerCase() === DELI_HOOK_CP_ADDRESS.toLowerCase()
  const hookAddress = isConstantProductHook ? DELI_HOOK_CP_ADDRESS : hooks ?? ZERO_ADDRESS
  const fee = isConstantProductHook ? Number(poolReq.fee) : DYNAMIC_FEE_FLAG

  const currency0 =
    token0Addr === ZERO_ADDRESS
      ? Ether.onChain(chainId)
      : new Token(chainId, token0Addr, await fetchDecimals(client, token0Addr))
  const currency1 =
    token1Addr === ZERO_ADDRESS
      ? Ether.onChain(chainId)
      : new Token(chainId, token1Addr, await fetchDecimals(client, token1Addr))

  // Pool state
  const {
    sqrtRatioX96: sqrtRatioX96Str,
    currentTick,
    poolLiquidity: poolLiquidityStr,
  } = await readPoolState(client as any, chainId, currency0 as any, currency1 as any, fee, tickSpacing, hookAddress)

  // Ticks (optional for increase; if missing, default to full range aligned to spacing)
  const providedTickLower = args.position?.tickLower
  const providedTickUpper = args.position?.tickUpper
  const rawTickLower = providedTickLower !== undefined ? Number(providedTickLower) : TickMath.MIN_TICK
  const rawTickUpper = providedTickUpper !== undefined ? Number(providedTickUpper) : TickMath.MAX_TICK
  const { tickLower, tickUpper } = alignTicks(rawTickLower, rawTickUpper, tickSpacing)

  // Amounts mapping
  const independentToken = (args as any).independentToken as 'TOKEN_0' | 'TOKEN_1' | undefined
  const independentAmount = args.independentAmount as string | undefined
  let finalAmount0 = '0'
  let finalAmount1 = '0'
  let dependentAmount: string | undefined
  if (independentToken && independentAmount) {
    const poolForMath = new Pool(
      currency0,
      currency1,
      fee,
      tickSpacing,
      hookAddress,
      sqrtRatioX96Str,
      poolLiquidityStr,
      currentTick,
    )
    const maxStr = MaxUint256.toString()
    // Use sentinel MaxUint for the dependent side to derive required amount, same as create path
    const amount0 = CurrencyAmount.fromRawAmount(currency0, independentToken === 'TOKEN_0' ? independentAmount : maxStr)
    const amount1 = CurrencyAmount.fromRawAmount(currency1, independentToken === 'TOKEN_1' ? independentAmount : maxStr)
    const tempPos = Position.fromAmounts({
      pool: poolForMath,
      tickLower,
      tickUpper,
      amount0: amount0.quotient,
      amount1: amount1.quotient,
      useFullPrecision: true,
    })
    if (independentToken === 'TOKEN_0') {
      finalAmount0 = independentAmount
      finalAmount1 = tempPos.amount1.quotient.toString()
      dependentAmount = finalAmount1
    } else {
      finalAmount1 = independentAmount
      finalAmount0 = tempPos.amount0.quotient.toString()
      dependentAmount = finalAmount0
    }
  } else {
    finalAmount0 = args.amount0 ?? '0'
    finalAmount1 = args.amount1 ?? '0'
  }

  if (isConstantProductHook) {
    const amount0Override = args.amount0 !== undefined ? BigInt(args.amount0) : undefined
    const amount1Override = args.amount1 !== undefined ? BigInt(args.amount1) : undefined
    const independentAmountRaw = independentAmount ? BigInt(independentAmount) : undefined
    const initialDependentRaw = ((args as any).initialDependentAmount as string | undefined)
      ? BigInt((args as any).initialDependentAmount as string)
      : undefined

    let computedAmount0 = amount0Override
    let computedAmount1 = amount1Override

    if (
      (computedAmount0 === undefined || computedAmount1 === undefined) &&
      independentToken &&
      independentAmountRaw !== undefined
    ) {
      if (initialDependentRaw !== undefined) {
        if (independentToken === 'TOKEN_0') {
          computedAmount0 = computedAmount0 ?? independentAmountRaw
          computedAmount1 = computedAmount1 ?? initialDependentRaw
        } else {
          computedAmount1 = computedAmount1 ?? independentAmountRaw
          computedAmount0 = computedAmount0 ?? initialDependentRaw
        }
      } else {
        const { reserve0, reserve1 } = await readConstantProductReserves(
          client as any,
          chainId,
          currency0 as any,
          currency1 as any,
          fee,
          tickSpacing,
        )

        if (reserve0 > BigInt(0) && reserve1 > BigInt(0)) {
          if (independentToken === 'TOKEN_0') {
            computedAmount0 = computedAmount0 ?? independentAmountRaw
            computedAmount1 = computedAmount1 ?? (independentAmountRaw * reserve1) / reserve0
          } else {
            computedAmount1 = computedAmount1 ?? independentAmountRaw
            computedAmount0 = computedAmount0 ?? (independentAmountRaw * reserve0) / reserve1
          }
        }
      }
    }

    if (computedAmount0 !== undefined || computedAmount1 !== undefined) {
      if (computedAmount0 !== undefined) {
        finalAmount0 = computedAmount0.toString()
      }
      if (computedAmount1 !== undefined) {
        finalAmount1 = computedAmount1.toString()
      }
      if (independentToken === 'TOKEN_0') {
        dependentAmount = finalAmount1
      } else if (independentToken === 'TOKEN_1') {
        dependentAmount = finalAmount0
      }
    }
  }

  // Build position + options
  const poolForTx = new Pool(
    currency0,
    currency1,
    fee,
    tickSpacing,
    hookAddress,
    sqrtRatioX96Str,
    poolLiquidityStr,
    currentTick,
  )
  console.log('position params', {
    tickLower,
    tickUpper,
    amount0: CurrencyAmount.fromRawAmount(currency0, finalAmount0).quotient,
    amount1: CurrencyAmount.fromRawAmount(currency1, finalAmount1).quotient,
    useFullPrecision: true,
  })
  const position = Position.fromAmounts({
    pool: poolForTx,
    tickLower,
    tickUpper,
    amount0: CurrencyAmount.fromRawAmount(currency0, finalAmount0).quotient,
    amount1: CurrencyAmount.fromRawAmount(currency1, finalAmount1).quotient,
    useFullPrecision: true,
  })

  const slippagePct = new Percent(Math.floor(Number((args as any).slippageTolerance ?? 0) * 100), 10_000)
  const latestBlock = await client.getBlock()
  const nowTs = Number(latestBlock.timestamp)
  const deadlineSec = (args as any).deadline ?? nowTs + 20 * 60
  const useNative = buildUseNative(chainId, currency0, currency1)

  if (isConstantProductHook) {
    const amount0DesiredRaw = BigInt(finalAmount0)
    const amount1DesiredRaw = BigInt(finalAmount1)

    const slippageNumerator = BigInt(slippagePct.numerator.toString())
    const slippageDenominator = BigInt(slippagePct.denominator.toString())

    const zeroBN = BigInt(0)

    const amount0MinRaw =
      amount0DesiredRaw === zeroBN || slippageDenominator === zeroBN
        ? amount0DesiredRaw
        : amount0DesiredRaw - (amount0DesiredRaw * slippageNumerator) / slippageDenominator

    const amount1MinRaw =
      amount1DesiredRaw === zeroBN || slippageDenominator === zeroBN
        ? amount1DesiredRaw
        : amount1DesiredRaw - (amount1DesiredRaw * slippageNumerator) / slippageDenominator

    const addLiquidityData = encodeFunctionData({
      abi: DELI_HOOK_CP_ABI,
      functionName: 'addLiquidity',
      args: [
        {
          currency0: token0Addr,
          currency1: token1Addr,
          fee,
          tickSpacing,
          hooks: DELI_HOOK_CP_ADDRESS,
        },
        {
          amount0Desired: amount0DesiredRaw,
          amount1Desired: amount1DesiredRaw,
          amount0Min: amount0MinRaw,
          amount1Min: amount1MinRaw,
          deadline: BigInt(deadlineSec),
          tickLower: 0,
          tickUpper: 0,
          userInputSalt: ZERO_BYTES32,
        },
      ],
    })

    let gasFee: string | undefined
    if (args.simulateTransaction) {
      gasFee = await estimateGasFee(client as any, walletAddress, DELI_HOOK_CP_ADDRESS, addLiquidityData)
    }

    const dependentForResponse =
      independentToken === 'TOKEN_0' ? amount1DesiredRaw.toString() : amount0DesiredRaw.toString()

    return {
      requestId: undefined,
      increase: {
        to: DELI_HOOK_CP_ADDRESS,
        from: walletAddress,
        data: addLiquidityData,
        value: '0',
        chainId: chainId as any,
      },
      gasFee,
      dependentAmount: dependentForResponse,
      poolLiquidity: poolLiquidityStr,
      currentTick,
      sqrtRatioX96: sqrtRatioX96Str,
    }
  }

  const options: any = {
    slippageTolerance: slippagePct,
    deadline: String(deadlineSec),
    hookData: '0x',
    tokenId: String(args.tokenId ?? ''),
    useNative,
  }

  const batchPermitData = (args as any)?.batchPermitData
  if (signature && batchPermitData?.values && walletAddress) {
    options.batchPermit = {
      owner: walletAddress,
      permitBatch: batchPermitData.values,
      signature,
    }
  }

  console.log('position', position)
  const { calldata, value } = V4PositionManager.addCallParameters(position, options)
  console.log('calldata', calldata)

  // simulate gas
  let gasFee: string | undefined
  if (args.simulateTransaction) {
    gasFee = await estimateGasFee(
      client as any,
      walletAddress,
      getV4PositionManagerAddress(chainId),
      calldata as `0x${string}`,
      value ? BigInt(value as any) : undefined,
    )
  }

  return {
    requestId: undefined,
    increase: {
      to: getV4PositionManagerAddress(chainId),
      from: walletAddress,
      data: calldata as string,
      value: value ? String(value) : '0',
      chainId: chainId as any,
    },
    gasFee,
    dependentAmount,
    poolLiquidity: poolLiquidityStr,
    currentTick,
    sqrtRatioX96: sqrtRatioX96Str,
  }
}

async function buildV4CreatePositionTx(
  args: CreateLPPositionRequest,
  signature?: string,
): Promise<ValidatedTransactionRequest> {
  const resp = await buildV4CreatePositionResponse(args, signature)
  const tx = resp.create
  if (!tx) throw new Error('create builder failed to produce tx')
  const valueStr = tx.value ?? '0'
  return {
    chainId: Number(args.chainId),
    from: tx.from,
    to: tx.to,
    data: tx.data,
    value: valueStr !== '0' ? BigInt(valueStr) : undefined,
  }
}

async function buildV4IncreasePositionTx(
  args: IncreaseLPPositionRequest,
  signature?: string,
): Promise<ValidatedTransactionRequest> {
  const resp = await buildV4IncreasePositionResponse(args, signature)
  const tx = resp.increase
  if (!tx) throw new Error('increase builder failed to produce tx')
  const valueStr = tx.value ?? '0'
  return {
    chainId: Number(args.chainId),
    from: tx.from,
    to: tx.to,
    data: tx.data,
    value: valueStr !== '0' ? BigInt(valueStr) : undefined,
  }
}
